import Phaser from 'phaser';
import { t } from '@i18n/index';
import { PALETTE, TEXT_COLOUR, titleStyle, readoutStyle, FONT, px, motionMs } from '../theme';
import { createButton } from '../ui/button';
import type { Campaign } from '@core/campaign/campaign';
import { HARD_SLOTS, type ClueSlot } from '@core/mystery/types';
import { maybeShowTutorial, createAdviserButton } from '../ui/adviserPanel';
import { createSettingsButton } from '../ui/settingsPanel';
import { layoutOf, watchResize } from '../ui/layout';
import { createScrollArea } from '../ui/scrollArea';
import { addBackdrop, EVIDENCE_BACKDROP } from '../assets';

/**
 * Die Beweislage.
 *
 * Bewusst **ohne jede Wertung**: keine Balken, keine Rangfolge, kein Hinweis
 * darauf, wofuer die Funde sprechen. Das Spiel legt die Fundstuecke vor und
 * schweigt — das Kombinieren ist die Aufgabe der Spielerin.
 *
 * Was bleibt, ist Ordnung ohne Deutung — Akt und Fundart als Etikett, damit
 * man die eigenen Notizen sortieren kann. Und neben jedem harten Fund, was er
 * gekostet hat: Er stammt von einem Wrack, an dem niemand mehr lebte.
 *
 * Die Liste scrollt. Frueher hoerte sie bei `height - 140` einfach auf — mit
 * vierzehn Hinweisen fielen ausgerechnet die aus Akt III heraus, die den Fall
 * entscheiden.
 */
export class EvidenceScene extends Phaser.Scene {
  constructor() {
    super('Evidence');
  }

  create(): void {
    const campaign = this.registry.get('campaign') as Campaign;
    this.cameras.main.setBackgroundColor(PALETTE.abyss);

    // Eine alte Seekarte als Unterlage. Sehr schwach, denn hier wird gelesen.
    addBackdrop(this, EVIDENCE_BACKDROP, 0.12);

    const layout = layoutOf(this);
    const { width, height, centreX } = layout;
    const columnWidth = Math.min(700, width - layout.margin * 2 - 12);
    const left = centreX - columnWidth / 2;

    const title = this.add.text(left, 20, t('evidence.title'), titleStyle(22));
    createSettingsButton(this, left + columnWidth, title.y + title.height / 2, () =>
      this.scene.restart(),
    );
    const hint = this.add.text(left, title.y + title.height + 6, t('evidence.hint'), {
      fontFamily: FONT.mono,
      fontSize: px(11),
      color: PALETTE.inkFaint,
      fontStyle: 'italic',
      wordWrap: { width: columnWidth - 120 },
    });
    createAdviserButton(this, left + columnWidth, hint.y + 8, campaign, 'evidence');

    const top = hint.y + hint.height + 18;
    const outstandingHeight = this.renderOutstanding(campaign, left, height - 76, columnWidth);
    const bottom = height - 76 - outstandingHeight - 12;

    if (campaign.ledger.size === 0) {
      this.add.text(left, top, t('evidence.empty'), {
        ...readoutStyle(13),
        color: PALETTE.inkFaint,
        fontStyle: 'italic',
      });
    } else {
      this.renderClues(campaign, left, top, columnWidth, Math.max(80, bottom - top));
    }

    const close = (): void => {
      this.scene.start('WorldMap');
    };
    createButton(this, centreX, height - 38, t('evidence.close'), close, {
      width: 200,
      fontSize: 12,
    });
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if (key === 'escape' || key === 'e' || key === 'b') close();
    });

    maybeShowTutorial(this, campaign, 'evidence');
    watchResize(this, () => this.scene.restart());
    this.cameras.main.fadeIn(motionMs(280), 5, 8, 13);
  }

  private renderClues(
    campaign: Campaign,
    left: number,
    top: number,
    columnWidth: number,
    viewHeight: number,
  ): void {
    const area = createScrollArea(this, { x: left, y: top, width: columnWidth, height: viewHeight });
    let y = 0;
    let lastAct = 0;

    for (const clue of campaign.ledger.all) {
      if (clue.act !== lastAct) {
        lastAct = clue.act;
        const act = this.add.text(0, y + 4, t('evidence.act', { act: clue.act }), {
          fontFamily: FONT.mono,
          fontSize: px(11),
          color: TEXT_COLOUR.coast,
        });
        area.content.add(act);
        y += act.height + 10;
      }

      // Eine Karte je Hinweis — sie scrollt als Ganzes.
      const parts: Phaser.GameObjects.GameObject[] = [];
      const slot = this.add.text(10, 0, t(`slot.${clue.slot}`), {
        fontFamily: FONT.mono,
        fontSize: px(10),
        color: PALETTE.inkFaint,
      });
      const source = this.add
        .text(columnWidth, 0, t('evidence.from', { source: campaign.sourceOfBeat(clue.beatIndex) }), {
          fontFamily: FONT.mono,
          fontSize: px(10),
          color: PALETTE.inkFaint,
        })
        .setOrigin(1, 0);
      parts.push(slot, source);
      let inner = Math.max(slot.height, source.height) + 3;

      const body = this.add.text(10, inner, t(clue.textKey, clue.params ?? {}), {
        ...readoutStyle(12),
        wordWrap: { width: columnWidth - 20 },
        lineSpacing: 2,
      });
      parts.push(body);
      inner += body.height;

      /*
       * Was er gekostet hat.
       *
       * Nur bei harten Funden: Fundstuecke, Spuren der Vermissten, Belege fuer
       * Dritte gibt es nur dort, wo niemand mehr zu retten war. Die Zahl steht
       * daneben, nuechtern, ohne Kommentar.
       */
      if ((HARD_SLOTS as readonly ClueSlot[]).includes(clue.slot)) {
        const entry = campaign.toll.entryForBeat(clue.beatIndex);
        if (entry !== null && entry.lost > 0) {
          const cost = this.add.text(
            10,
            inner + 4,
            t(entry.avoidable ? 'evidence.cost.avoidable' : 'evidence.cost', {
              count: entry.lost,
              name: entry.vesselName,
            }),
            {
              fontFamily: FONT.mono,
              fontSize: px(10),
              color: entry.avoidable ? TEXT_COLOUR.danger : PALETTE.inkDim,
              fontStyle: 'italic',
              wordWrap: { width: columnWidth - 20 },
            },
          );
          parts.push(cost);
          inner += cost.height + 4;
        }
      }

      const card = this.add.container(0, y, parts);
      card.setSize(columnWidth, inner);
      area.content.add(card);
      y += inner + 16;
    }

    area.finish();
  }

  /**
   * Was der laufende Akt noch braucht — als Fundart, nicht als Auflösung.
   * Liefert die Hoehe des Blocks; er steht ueber der Schaltflaeche.
   */
  private renderOutstanding(
    campaign: Campaign,
    left: number,
    bottom: number,
    columnWidth: number,
  ): number {
    const outstanding = campaign.outstandingEvidenceKinds;
    if (outstanding.length === 0) return 0;

    const list = this.add
      .text(left, 0, outstanding.map((slot) => t(`slot.${slot}`)).join('  ·  '), {
        fontFamily: FONT.mono,
        fontSize: px(12),
        color: TEXT_COLOUR.landmark,
        wordWrap: { width: columnWidth },
      })
      .setOrigin(0, 1);
    list.y = bottom;

    const label = this.add
      .text(left, list.y - list.height - 4, t('evidence.outstanding'), {
        fontFamily: FONT.mono,
        fontSize: px(11),
        color: PALETTE.inkFaint,
      })
      .setOrigin(0, 1);

    this.add
      .rectangle(left, label.y - label.height - 8, columnWidth, 1, PALETTE.panelEdge)
      .setOrigin(0, 0.5);

    return list.height + label.height + 16;
  }
}
